/**
 * PhysicianDashboardPage — landing page for the physician role (Task #63,
 * Jun 8 2026). Mirrors PEMDashboardPage for the diagnosis half of
 * Carlos's spec.
 *
 * Surfaces the consult queue: contacts currently at
 * consult_scheduled or consult_completed, with their candidacy status
 * and assigned PEM. Clicking a row navigates to the CRM contact detail
 * surface and opens the Physician tab.
 *
 * Permission: physician:read.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Stethoscope, AlertTriangle, CheckCircle2, Clock, Loader2,
  RefreshCcw, ChevronRight, Shield,
} from 'lucide-react';

const VERDICT_COLOR = {
  yes: '#62BB46',
  no: '#DC3545',
  conditional: '#F26522',
  pending: '#6C757D',
};
const VERDICT_LABEL = {
  yes: 'Candidate',
  no: 'Not a candidate',
  conditional: 'Conditional',
  pending: 'Pending',
};

export default function PhysicianDashboardPage({ user, onNavigate }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Task #100 — Phase 4f: signing inbox feed
  const [signingInbox, setSigningInbox] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await window.zignagenixAPI.invoke('physician:dashboard:queue', { limit: 100 });
      if (r && r.error) throw new Error(r.message || r.code);
      setQueue(Array.isArray(r) ? r : []);

      // Pull the counter-sign inbox in parallel — gracefully degrade if
      // the channel isn't registered yet (mid-rollout).
      try {
        const inbox = await window.zignagenixAPI.invoke('treatment-proposals:awaiting-counter-sign');
        if (Array.isArray(inbox)) setSigningInbox(inbox);
      } catch (_) {
        // Don't fail the whole dashboard if the inbox call errors.
        setSigningInbox([]);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // ─── KPIs (computed from queue) ──────────────────────────────────
  const pendingConsults = queue.filter(q => q.current_stage === 'consult_scheduled').length;
  const completedAwaitingCandidacy = queue.filter(
    q => q.current_stage === 'consult_completed' && (!q.candidacy_signed_at || q.verdict !== 'yes')
  ).length;
  const clearedToProceed = queue.filter(
    q => q.candidacy_signed_at && q.verdict === 'yes'
  ).length;
  const conditionalPending = queue.filter(
    q => q.candidacy_signed_at && q.verdict === 'conditional'
  ).length;

  // ─── Drill-in: navigate to CRM and open the Physician tab on this contact ─
  function openContact(contactId) {
    /*
     * ⛔⛔ A ROW WITH NO CONTACT MUST NOT NAVIGATE — Card P114 (B3d).
     *
     * Without this, `openContact(undefined)` wrote the STRING "undefined" into
     * sessionStorage and then navigated to the CRM, which opened no record.
     * The person got a page change and nothing else — reported as a dead
     * click. The rows above also carried `cursor: pointer` unconditionally, so
     * every row looked equally clickable.
     *
     * ⚠ The guard is here AND at both call sites, and they are NOT equals.
     *
     * MEASURED (Card P114 red arms): deleting THIS line leaves the suite
     * fully green, because both call sites already withhold `onClick` when
     * `contact_id` is null — so today nothing in the UI can reach it. The
     * arm with teeth is the call-site one: restoring the unconditional
     * `cursor: 'pointer'` goes red immediately.
     *
     * It stays anyway, and this comment is why rather than a claim that it
     * is load-bearing: this is the only place that writes the cross-page
     * handoff key, the next caller will not remember the rule, and the
     * failure it prevents is silent — `String(undefined)` is a perfectly
     * valid string and the CRM simply opens with nothing selected. Belt and
     * braces, labelled as braces.
     */
    if (contactId == null) return;
    try {
      sessionStorage.setItem('cowork.crm.openContact', String(contactId));
      sessionStorage.setItem('cowork.crm.openTab', 'physician');
    } catch { /* sessionStorage unavailable — fall through */ }
    if (typeof onNavigate === 'function') onNavigate('crm');
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <Stethoscope size={28} color="#2E8B98" />
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#212529' }}>Physician Dashboard</h1>
          <div style={{ fontSize: 12, color: '#6C757D' }}>
            Consult queue, candidacy verdicts, and treatment plans.{' '}
            <em>The Physician Diagnoses. The PEM Coordinates.</em>
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={reload} disabled={loading} style={refreshBtn()}>
            <RefreshCcw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* ─── KPI strip ─── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12, marginBottom: 20,
      }}>
        <Kpi label="Consults scheduled" value={pendingConsults} icon={<Clock size={18} />} color="#2E8B98" />
        <Kpi label="Completed — candidacy pending" value={completedAwaitingCandidacy} icon={<AlertTriangle size={18} />} color="#F26522" />
        <Kpi label="Cleared (verdict yes)" value={clearedToProceed} icon={<CheckCircle2 size={18} />} color="#62BB46" />
        <Kpi label="Conditional (PEM coordinating)" value={conditionalPending} icon={<Shield size={18} />} color="#7C3AED" />
      </div>

      {/* ─── Task #100 — Phase 4f: Signing Inbox ─── */}
      {/* Renders only when there are patient-signed proposals awaiting counter-sign. */}
      {signingInbox.length > 0 && (
        <div style={{
          background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)',
          border: '2px solid #F59E0B', overflow: 'hidden', marginBottom: 20,
        }}>
          <div style={{
            padding: '12px 16px', background: '#FEF3C7', borderBottom: '1px solid #F59E0B',
            fontSize: 14, fontWeight: 700, color: '#92400E',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Shield size={14} color="#F59E0B" /> Signing inbox — awaiting your counter-sign
            <span style={{
              marginLeft: 6, background: '#F59E0B', color: 'white',
              fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 700,
            }}>{signingInbox.length}</span>
          </div>
          <div>
            {signingInbox.map(p => {
              const waitingDays = p.patient_signed_at
                ? Math.floor((Date.now() - new Date(p.patient_signed_at).getTime()) / 86400000)
                : null;
              const urgent = waitingDays != null && waitingDays >= 2;
              return (
                <div
                  key={p.id}
                  onClick={p.contact_id != null ? () => openContact(p.contact_id) : undefined}
                  title={p.contact_id == null ? 'No CRM contact is linked to this record yet' : undefined}
                  style={{
                    padding: '12px 16px', borderBottom: '1px solid #FEE2BC',
                    cursor: p.contact_id != null ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: urgent ? '#FEF7E8' : 'white',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: '#1F2937',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {p.patient_name || `Contact #${p.contact_id}`}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                      Proposal #{p.id} ·{' '}
                      {p.recommended_label || (p.modality_category || '').replace(/_/g, ' ')}
                      {p.total_amount_cents > 0 && (
                        <> · ${(p.total_amount_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}</>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: urgent ? '#DC2626' : '#92400E', textAlign: 'right' }}>
                    {waitingDays === 0 ? 'Today' :
                     waitingDays === 1 ? '1 day ago' :
                     waitingDays != null ? `${waitingDays} days ago` : 'just now'}
                    <div style={{ marginTop: 2, fontWeight: 600 }}>
                      {p.patient_sig_count} sig{p.patient_sig_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <ChevronRight size={16} color="#92400E" style={{ flexShrink: 0 }} />
                </div>
              );
            })}
          </div>
          <div style={{
            padding: '10px 16px', background: '#FEF7E8',
            fontSize: 11, color: '#92400E', borderTop: '1px solid #FEE2BC',
          }}>
            Click a row to open the contact's Physician tab and counter-sign there.
          </div>
        </div>
      )}

      {/* ─── Queue table ─── */}
      <div style={{
        background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)',
        border: '1px solid #E9ECEF', overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #E9ECEF',
          fontSize: 14, fontWeight: 700, color: '#212529',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Stethoscope size={14} color="#2E8B98" /> Consult queue
          <span style={{ fontSize: 11, fontWeight: 400, color: '#6C757D', marginLeft: 6 }}>
            ({queue.length})
          </span>
        </div>

        {loading && (
          <div style={{ padding: 32, textAlign: 'center', color: '#6C757D' }}>
            <Loader2 size={18} className="spin" style={{ verticalAlign: 'middle' }} />
            <span style={{ marginLeft: 6 }}>Loading consult queue…</span>
          </div>
        )}

        {err && (
          <div style={{ padding: 16, background: '#FEE2E2', color: '#991B1B', fontSize: 13 }}>
            <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> {err}
          </div>
        )}

        {!loading && !err && queue.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#6C757D', fontSize: 13 }}>
            No patients currently in the consult queue.
            <div style={{ fontSize: 11, color: '#ADB5BD', marginTop: 6 }}>
              The PEM advances contacts to <code>consult_scheduled</code> when a consult is booked.
              They will appear here for evaluation.
            </div>
          </div>
        )}

        {!loading && !err && queue.length > 0 && (
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr 40px',
              padding: '8px 16px', background: '#F8F9FA', fontSize: 11,
              color: '#6C757D', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}>
              <div>Patient</div>
              <div>Stage</div>
              <div>Verdict</div>
              <div>PEM Owner</div>
              <div>In Stage Since</div>
              <div></div>
            </div>
            {queue.map(row => {
              const verdict = row.verdict || 'pending';
              const enteredAt = row.entered_stage_at ? new Date(row.entered_stage_at) : null;
              const daysIn = enteredAt
                ? Math.floor((Date.now() - enteredAt.getTime()) / (24 * 60 * 60 * 1000))
                : null;
              return (
                <div key={row.contact_id}
                  onClick={row.contact_id != null ? () => openContact(row.contact_id) : undefined}
                  title={row.contact_id == null ? 'No CRM contact is linked to this record yet' : undefined}
                  style={{
                    display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr 40px',
                    padding: '10px 16px', borderBottom: '1px solid #F1F3F5',
                    fontSize: 13, alignItems: 'center',
                    cursor: row.contact_id != null ? 'pointer' : 'default',
                    transition: 'background .1s',
                  }}
                  onMouseEnter={e => { if (row.contact_id != null) e.currentTarget.style.background = '#F8F9FA'; }}
                  onMouseLeave={e => { if (row.contact_id != null) e.currentTarget.style.background = 'white'; }}
                >
                  <div>
                    <strong>{row.last_name}, {row.first_name}</strong>
                    {row.email && <div style={{ fontSize: 10, color: '#ADB5BD' }}>{row.email}</div>}
                  </div>
                  <div>{labelForStage(row.current_stage)}</div>
                  <div>
                    {row.candidacy_signed_at ? (
                      <span style={{
                        background: VERDICT_COLOR[verdict] || '#6C757D',
                        color: 'white', padding: '2px 8px', borderRadius: 999,
                        fontSize: 10, fontWeight: 700,
                      }}>{VERDICT_LABEL[verdict] || verdict}</span>
                    ) : row.current_candidacy_id ? (
                      <span style={{ fontSize: 11, color: '#F26522' }}>
                        <Clock size={11} style={{ verticalAlign: 'middle' }} /> Draft
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#ADB5BD' }}>Not started</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#6C757D' }}>{row.assigned_to_name || '—'}</div>
                  <div style={{ fontSize: 11, color: '#6C757D' }}>
                    {daysIn !== null ? `${daysIn}d` : '—'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <ChevronRight size={14} color="#ADB5BD" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, color }) {
  return (
    <div style={{
      background: 'white', borderRadius: 10, padding: 14,
      boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid #E9ECEF',
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        background: `${color}18`, color, padding: 8, borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#6C757D', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#212529' }}>{value}</div>
      </div>
    </div>
  );
}

function labelForStage(stage) {
  const labels = {
    consult_scheduled: 'Consult Scheduled',
    consult_completed: 'Consult Completed',
  };
  return labels[stage] || stage;
}

function refreshBtn() {
  return {
    background: 'white', border: '1px solid #DEE2E6', color: '#212529',
    padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  };
}
